// client/src/routes/app/contracts/$contractId/edit.tsx — SCR-CON-002 over a DRAFT · CON-005.
//
// The one in-place edit a contract has. CON-001's D-594: `PATCH /contracts/{id}` takes a draft's
// terms as a patch and answers 422 `contract-requires-amendment` on anything else — after
// activation the terms move only through an act adițional (BR-RENT-05). So this route is guarded
// twice: by the status, which sends an ACTIVE contract to its detail rather than rendering a
// form the API will refuse, and by the server, which refuses it anyway.
//
// It is the SAME wizard as `new.tsx`, prefilled — the terms of a draft are the terms of a
// contract, and a second form would be a second copy of `contractSchema` to keep in step with
// 03 §8.4. Nested under `$contractId` rather than beside it, so the URL says which contract is
// being edited and a refresh lands back on the same form; it is not a tab, because the tabs are
// readings of the contract and this is a write.

import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';

import { PageHeader } from '@/components/layout/PageHeader';
import { RequirePermission } from '@/components/layout/route-guards';
import { ErrorState, LoadingState } from '@/components/ui/states';
import { ContractWizard } from '@/features/contracts/ContractWizard';
import { useContract, useUpdateDraftContract } from '@/features/contracts/queries';
import {
  toContractForm,
  toContractInput,
  type ContractFormValues,
} from '@/features/contracts/schema';
import { toast } from '@/stores/toast-store';

export const Route = createFileRoute('/app/contracts/$contractId/edit')({
  component: EditDraftScreen,
});

function EditDraftScreen() {
  const { contractId } = Route.useParams();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const contract = useContract(contractId);
  const update = useUpdateDraftContract();

  if (contract.isPending) return <LoadingState rows={4} />;
  if (contract.isError)
    return <ErrorState error={contract.error} onRetry={() => void contract.refetch()} />;

  const detail = contract.data;

  const back = async () => {
    await navigate({ to: '/app/contracts/$contractId', params: { contractId } });
  };

  if (detail.status !== 'DRAFT') {
    return (
      <>
        <PageHeader title={detail.contractNumber} description={t('contracts.form.editTitle')} />
        <div className="alert alert-warning text-sm" role="alert">
          {t('contracts.errors.requiresAmendment')}
        </div>
      </>
    );
  }

  return (
    <RequirePermission permission="Leasing.Write">
      <PageHeader
        title={t('contracts.form.editTitle')}
        description={detail.contractNumber}
        backTo="/app/contracts/$contractId"
        backLabel={detail.contractNumber}
      />
      <ContractWizard
        initialValues={toContractForm(detail)}
        submitLabel={t('common.save')}
        onSubmit={async (values: ContractFormValues) => {
          // `ContractDraftPatchInput` reads an absent member as KEEP; the wizard collects every
          // term, so the whole body is sent and the patch is a full replacement of the draft.
          await update.mutateAsync({ path: { id: contractId }, body: toContractInput(values) });
          toast.success(t('contracts.form.saved'));
          await back();
        }}
        onCancel={() => {
          void back();
        }}
      />
    </RequirePermission>
  );
}
